/**
 * Карточка проекта (FR-9) — модальное окно поверх подраздела: реквизиты,
 * сроки, статус, суммы план/факт всего и за год с % освоения, заказчик
 * (АБП), бюджетная классификация и список договоров объекта (сумма,
 * способ закупки, % освоения — FR-9.2).
 *
 * Drill-through до первичных данных — переход в дашборд Power BI; боевая
 * интеграция мокается (CLAUDE.md §6), поэтому кнопка неактивна: без
 * первичных данных — по FR-3.4, с данными — до появления интеграции.
 */
import { useTranslation } from 'react-i18next';

import { fmt, fmt1 } from './format';
import { StatusPill } from './StatusPill';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import type { InfrastructureProject } from '../../../types/projects';

interface MoneyCellProps {
  label: string;
  planMln: number | null;
  factMln: number | null;
  /** Подпись вместо сумм, когда годового плана нет (проект прошлых лет). */
  emptyLabel?: string;
}

const MoneyCell = ({ label, planMln, factMln, emptyLabel }: MoneyCellProps) => {
  const { t } = useTranslation();
  const pct =
    planMln != null && planMln > 0 ? ((factMln ?? 0) / planMln) * 100 : null;
  return (
    <div className="rounded-lg border border-surface-border p-3">
      <p className="text-[11px] uppercase tracking-wide text-text-faint">
        {label}
      </p>
      {planMln == null || planMln === 0 ? (
        <p className="mt-1 text-sm text-text-muted">{emptyLabel ?? '—'}</p>
      ) : (
        <>
          <p className="mt-1 text-lg font-bold tabular-nums">
            {fmt(planMln)} / {fmt(factMln ?? 0)}{' '}
            <span className="text-xs font-medium text-text-muted">
              {t('projects.units.mlnTg')} ·{' '}
              {t('projects.infrastructure.registry.development', {
                pct: Math.round(pct ?? 0),
              })}
            </span>
          </p>
          <span className="mt-2 block h-2 overflow-hidden rounded-full bg-surface-muted">
            <span
              className="block h-full rounded-full bg-brand-500"
              style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
            />
          </span>
        </>
      )}
    </div>
  );
};

interface ProjectCardDialogProps {
  project: InfrastructureProject | null;
  /** Год данных подраздела — подпись блока «год · план / факт». */
  year: number | null;
  onClose: () => void;
}

export const ProjectCardDialog = ({
  project,
  year,
  onClose,
}: ProjectCardDialogProps) => {
  const { t } = useTranslation();
  if (project === null) return null;

  const workLabel = (key: string): string =>
    t(`projects.infrastructure.works.${key}`, { defaultValue: key });
  const procurementLabel = (key: string | null): string =>
    key === null
      ? '—'
      : t(`projects.infrastructure.procurementNames.${key}`, {
          defaultValue: key,
        });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-y-auto"
        data-testid="projects-card"
      >
        <DialogHeader>
          <DialogTitle className="pr-6 text-left leading-snug">
            {project.name}
          </DialogTitle>
          <DialogDescription className="text-left">
            {[
              project.territory,
              `${t('projects.infrastructure.card.work')}: ${workLabel(project.work)}`,
              `${t('projects.infrastructure.registry.procurement')}: ${procurementLabel(project.procurement)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-x-4 gap-y-3 sm:grid-cols-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-text-faint">
              {t('projects.infrastructure.card.period')}
            </p>
            <p className="mt-0.5 text-sm font-semibold tabular-nums">
              {project.period ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-text-faint">
              {t('projects.infrastructure.registry.status')}
            </p>
            <p className="mt-0.5">
              <StatusPill status={project.status} />
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-text-faint">
              {t('projects.infrastructure.card.abp')}
            </p>
            <p className="mt-0.5 text-sm font-semibold">{project.abp ?? '—'}</p>
          </div>
          {project.budgetClassification !== null && (
            <div className="sm:col-span-3">
              <p className="text-[11px] uppercase tracking-wide text-text-faint">
                {t('projects.infrastructure.card.budget')}
              </p>
              <p className="mt-0.5 text-sm font-semibold">
                {project.budgetClassification}
              </p>
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <MoneyCell
            label={t('projects.infrastructure.card.totalMoney')}
            planMln={project.sumMln}
            factMln={project.factTotalMln}
          />
          <MoneyCell
            label={t('projects.infrastructure.card.yearMoney', {
              year: year ?? new Date().getFullYear(),
            })}
            planMln={project.planYearMln}
            factMln={project.factYearMln}
            emptyLabel={t('projects.infrastructure.card.completedEarlier')}
          />
        </div>

        {project.contracts.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-faint">
              {t('projects.infrastructure.card.contracts')}
            </h4>
            <ul data-testid="projects-card-contracts">
              {project.contracts.map((contract) => (
                <li
                  key={contract.name}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-surface-border py-2 text-sm last:border-b-0"
                >
                  <span className="w-24 shrink-0">
                    <span className="rounded bg-brand-50 px-1.5 py-0.5 text-xs font-semibold text-brand-700">
                      {workLabel(contract.type)}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1 text-text-muted">
                    {contract.name}
                  </span>
                  <span className="whitespace-nowrap font-semibold tabular-nums">
                    {contract.sumMln != null
                      ? `${fmt1(contract.sumMln)} ${t('projects.units.mlnTg')}`
                      : '—'}
                  </span>
                  <span className="w-36 shrink-0 whitespace-nowrap text-text-muted">
                    {procurementLabel(contract.procurement)}
                  </span>
                  <span className="w-12 shrink-0 text-right tabular-nums">
                    {contract.developmentPct != null
                      ? `${contract.developmentPct}%`
                      : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button disabled data-testid="projects-card-drill">
            {t('projects.infrastructure.card.drill')}
          </Button>
          <span className="text-xs text-text-faint">
            {project.hasPrimaryData
              ? t('projects.infrastructure.card.drillSoon')
              : t('projects.infrastructure.card.drillNoData')}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
